import { create } from "zustand";

/**
 * Why a preview ended LOCALLY (not by the user's own "Exit"):
 *  - "sessionExpired": a request riding the CURRENT preview bearer got 401
 *    `sessionExpired` — the token was revoked (the main tab re-minted or
 *    switched, which supersedes a copy tab's token) or expired server-side;
 *  - "expired": the tab's own self-stop shortly before the session's end.
 */
export type PreviewEndReason = "sessionExpired" | "expired";

/**
 * B2-0 audit r1 (security F1): an UNPLANNED end — the bearer died under the tab
 * (`sessionExpired`). It blocks EVERY tab of that event until a reload (the
 * main tab too, not a toast) and leaves the request tombstone in
 * `previewStore`: a flow that started under the preview and carries on after a
 * caught error must never continue on the admin cookie. The expiry self-stop
 * (`expired`) is planned: it fires while the token is still valid, the main
 * tab gets the toast and keeps working as the admin.
 */
export function isUnplannedEnd(reason: PreviewEndReason): boolean {
  return reason === "sessionExpired";
}

/**
 * B2-0 audit r2 (security F2): does this end BLOCK its tab until a reload? An
 * unplanned end blocks every tab; ANY local end blocks a copy tab (it did not
 * start the session — the expiry self-stop included, also after the copy tab's
 * own re-mint or switch, audit r3 L1). Exactly these ends leave
 * the request tombstone in `previewStore`: a tab that demands a reload must not
 * keep talking to the event as the admin behind the notice (a write loop that
 * straddles the self-stop). Only the starting tab's planned expiry (toast, keeps
 * working) leaves none.
 */
export function endBlocks(e: Pick<PreviewEnded, "wasOwner" | "reason">): boolean {
  return !e.wasOwner || isUnplannedEnd(e.reason);
}

export interface PreviewEnded {
  /** The event the ended session previewed. */
  eventId: string | null;
  /** Whether THIS tab started the session (the main tab) or inherited it (a
   *  builder / copy tab opened from the preview — also after its own re-mint
   *  or switch: `previewStore`'s `startedHere`, audit r3 L1). */
  wasOwner: boolean;
  reason: PreviewEndReason;
}

/**
 * Tiny UI store that remembers a LOCAL end of the preview until a host has
 * dealt with it (B2-0, decision 3). `client.ts` cannot toast and `useToast` is
 * pure React context, so the store is the hand-over: `endPreviewLocally` (in
 * `previewStore`) sets it, `PreviewEndedNotice` reads it — a toast in the main
 * tab, a blocking "reload" notice in a tab that silently became the admin.
 *
 * Kept apart from `usePreviewStore` for the same reason as `previewBlockStore`:
 * that store owns the crucial token lifecycle and stays free of transient UI.
 */
interface PreviewEndedState {
  ended: PreviewEnded | null;
  end: (e: PreviewEnded) => void;
  /** A host handled it (toast shown, or not its event). */
  ack: () => void;
}

export const usePreviewEndedStore = create<PreviewEndedState>((set) => ({
  ended: null,
  end: (e) => set({ ended: e }),
  ack: () => set({ ended: null }),
}));
